/** Human wording for windows and spans — shared by the panel's readout, the toolbar
 *  trigger and the QuerySearch chips, so a window reads the same everywhere. */
import { format, isSameDay, isSameYear } from 'date-fns';

import { isNow, isRelative, relativeBoundPhrase, relativeWindowPhrase } from './relative';
import { instantToWallClock } from './timezone';
import type { TimeBound, TimeWindowTz } from './types';

const ZONE_WORD: Record<TimeWindowTz, string> = { utc: 'UTC', local: 'local time' };

const DURATION_UNITS = [
  ['d', 86_400],
  ['h', 3_600],
  ['m', 60],
  ['s', 1],
] as const;

/** "2 d 3 h", "1 h 30 m", "45 m", "30 s" — the largest two non-zero units; seconds
 *  only under ten minutes. */
export function formatDuration(ms: number): string {
  let s = Math.max(0, Math.round(ms / 1000));
  if (s >= 600) s -= s % 60;
  const parts: string[] = [];
  for (const [unit, size] of DURATION_UNITS) {
    const n = Math.floor(s / size);
    s %= size;
    if (n > 0) parts.push(`${n} ${unit}`);
  }
  return parts.slice(0, 2).join(' ') || '0 s';
}

function timeText(wall: Date): string {
  return format(wall, wall.getSeconds() === 0 ? 'HH:mm' : 'HH:mm:ss');
}

function dayText(wall: Date, withYear: boolean): string {
  return format(wall, withYear ? 'd MMM yyyy' : 'd MMM');
}

function isStartOfDay(wall: Date): boolean {
  return wall.getHours() === 0 && wall.getMinutes() === 0 && wall.getSeconds() === 0;
}

function isEndOfDay(wall: Date): boolean {
  return wall.getHours() === 23 && wall.getMinutes() === 59 && wall.getSeconds() === 59;
}

interface WindowLabelOptions {
  tz?: TimeWindowTz;
  /** Append the zone ("UTC") to wordings that show fixed clock times or days. */
  zone?: boolean;
  /** Reference "now" for the current-year check. */
  now?: Date;
}

/** One line for a window, lower-case:
 *  "last 15 minutes" · "next 2 days" · "since 21 Oct 14:00" · "21 Oct 14:00 → 15:00" ·
 *  "21–22 Oct" · "from 21 Oct 14:00" · "until 21 Oct 15:00". Null when both are empty. */
export function windowLabel(
  from: TimeBound | null,
  to: TimeBound | null,
  { tz = 'utc', zone = false, now = new Date() }: WindowLabelOptions = {},
): string | null {
  if (from === null && to === null) return null;
  const phrase = relativeWindowPhrase(from, to);
  if (phrase) return phrase;

  const wf = from instanceof Date ? instantToWallClock(from, tz) : null;
  const wt = to instanceof Date ? instantToWallClock(to, tz) : null;
  const wallNow = instantToWallClock(now, tz);
  const withYear = [wf, wt].some((w) => w !== null && !isSameYear(w, wallNow));
  const suffix = zone ? ` ${ZONE_WORD[tz]}` : '';
  const fixed = (w: Date) => `${dayText(w, withYear)} ${timeText(w)}`;

  if (wf && wt) {
    if (isStartOfDay(wf) && isEndOfDay(wt)) {
      if (isSameDay(wf, wt)) return `${dayText(wf, withYear)}${suffix}`;
      const sameMonth = wf.getMonth() === wt.getMonth() && wf.getFullYear() === wt.getFullYear();
      return sameMonth
        ? `${format(wf, 'd')}–${dayText(wt, withYear)}${suffix}`
        : `${dayText(wf, withYear)} – ${dayText(wt, withYear)}${suffix}`;
    }
    return isSameDay(wf, wt)
      ? `${fixed(wf)} → ${timeText(wt)}${suffix}`
      : `${fixed(wf)} → ${fixed(wt)}${suffix}`;
  }
  if (wf && isNow(to)) return `since ${fixed(wf)}${suffix}`;
  if (wf && to === null) return `from ${fixed(wf)}${suffix}`;
  if (wt && from === null) return `until ${fixed(wt)}${suffix}`;
  if (isRelative(from) && to === null) return `from ${relativeBoundPhrase(from)}`;
  if (isRelative(to) && from === null) return `until ${relativeBoundPhrase(to)}`;

  // Mixed shapes typed by hand (now-2h..now-1h, now-1d.."2026-10-21 12:00", …).
  const part = (b: TimeBound | null, w: Date | null) =>
    b === null ? '…' : isRelative(b) ? relativeBoundPhrase(b) : `${fixed(w!)}${suffix}`;
  return `${part(from, wf)} → ${part(to, wt)}`;
}

/** Whether the window moves on refresh (has a relative bound). */
export function isLiveWindow(from: TimeBound | null, to: TimeBound | null): boolean {
  return isRelative(from) || isRelative(to);
}

/** Capitalise the first letter ("last 15 minutes" → "Last 15 minutes"). */
export function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
